import type { TableMetadataResult } from '../../../../types/electron';
import { resolveSingleSourceTable } from './insertStatementBuilder';

/**
 * IDENTITY / auto-increment column lookup for generated INSERTs (Msg 544).
 *
 * Both results grids need the same thing at copy/export time: "which columns of
 * this table are IDENTITY?". The answer comes from ONE light `getTableMetadata`
 * call per action (never per row) — `detailed: false` skips constraints,
 * indexes and partition fetches, keeping it to the columns query the identity
 * flag already rides on.
 *
 * FIX-1 (CP-2) — three-outcome contract, not a flat `[]` on every non-success
 * path. Callers need to tell "we checked and there's nothing to wrap" apart
 * from "we couldn't check":
 *   - `[]` — the table resolved and the metadata genuinely reports no IDENTITY
 *     columns. Silent: the builder emits exactly what it always has.
 *   - `[]` — no single source table could be resolved (joins / expressions /
 *     multi-table projections). Silent by design — identity awareness has
 *     never applied to those, and never will.
 *   - `null` — the table WAS resolved (we know which table to ask about) but
 *     the check itself failed: no `getTableMetadata` API, `success: false`,
 *     a thrown/rejected call, or missing connection/database context. Callers
 *     that care (the "Skip identity columns" toggle is on, or the engine is
 *     MSSQL where the wrapper is the default) must degrade LOUDLY — warn the
 *     user instead of silently emitting a script that may hit Msg 544.
 */

type ResultSetColumnSource = {
  sourceSchema?: string;
  sourceTable?: string;
  isComputed?: boolean;
  /** Other column fields (name, dataType, …) are irrelevant here but allowed through. */
  [key: string]: unknown;
};

type ElectronMetadataApi = {
  getTableMetadata?: (request: {
    connectionId: string;
    database: string;
    schema: string;
    tableName: string;
    detailed?: boolean;
  }) => Promise<TableMetadataResult>;
};

function getMetadataApi(): ElectronMetadataApi | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as { electronAPI?: ElectronMetadataApi }).electronAPI;
}

/**
 * Identity column names of an explicitly known table (Table Explorer's case).
 *
 * Returns `null` when the table is known but the check couldn't be completed
 * (see the module doc above) — never conflate that with "checked, found
 * none" (`[]`).
 */
export async function fetchIdentityColumns(args: {
  connectionId?: string | null;
  database?: string | null;
  schema?: string;
  table: string;
}): Promise<string[] | null> {
  const { connectionId, database, schema, table } = args;
  // No table to ask about at all — mirrors the "no single source table"
  // silence, not a failed check.
  if (!table) return [];
  // The table IS known, but we lack the context to check it → failed check.
  if (!connectionId || !database) return null;

  const api = getMetadataApi();
  if (!api?.getTableMetadata) return null;

  try {
    const result = await api.getTableMetadata({
      connectionId,
      database,
      schema: schema ?? '',
      tableName: table,
      detailed: false,
    });
    if (!result?.success || !result.metadata) return null;
    return (result.metadata.columns ?? [])
      .filter((column) => column.isIdentity)
      .map((column) => column.name);
  } catch {
    // Offline / IPC unavailable / provider error → failed check.
    return null;
  }
}

/**
 * Identity column names for a Query Editor result set. Resolves the single
 * source table from column provenance first — joins, expressions and
 * multi-table projections resolve to `[]` (silent by design, skips the fetch
 * entirely) rather than `null` (which means "we tried and failed").
 */
export async function fetchIdentityColumnsForResultSet(args: {
  connectionId?: string | null;
  database?: string | null;
  columns: readonly ResultSetColumnSource[];
}): Promise<string[] | null> {
  const source = resolveSingleSourceTable(args.columns);
  if (!source) return [];

  return fetchIdentityColumns({
    connectionId: args.connectionId,
    database: args.database,
    schema: source.schema,
    table: source.table,
  });
}
